// AccountOverview.jsx
import React from "react";

/**
 * Props:
 *  - userData        : object  — { name, email, created_at }
 *  - loadingUser     : bool
 *  - statsLoading    : bool
 *  - cartCount       : number
 *  - wishlistCount   : number
 *  - ordersCount     : number
 *  - cartSubtotal    : number
 *  - memberSince     : string
 *  - msg             : { type: "ok"|"err", text: string }
 *  - onRefresh       : () => void
 *  - onGoToSettings  : () => void
 *  - onGoToOrders    : () => void
 *  - onGoToWishlist  : () => void
 *  - onGoToCart      : () => void
 *  - onGoToDeliveryAddress : () => void
 */
const AccountOverview = ({
  userData,
  loadingUser,
  statsLoading,
  cartCount,
  wishlistCount,
  ordersCount,
  cartSubtotal,
  memberSince,
  msg,
  onRefresh,
  onGoToSettings,
  onGoToOrders,
  onGoToWishlist,
  onGoToCart,
  onGoToDeliveryAddress,
}) => {
  return (
    <>
      {/* ── Topbar ── */}
      <div className="ap-topbar">
        <span className="ap-topbar-title">Account Overview</span>
        <div className="ap-topbar-right">
          <button className="ap-btn-sm outline" onClick={onRefresh} disabled={statsLoading}>
            <i className="ph ph-arrows-clockwise" />
            {statsLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="ap-body">
        {msg?.text && (
          <div className={`ap-alert ${msg.type === "err" ? "err" : "ok"}`}>
            <i
              className={`ph ${msg.type === "err" ? "ph-warning-circle" : "ph-check-circle"}`}
              style={{ fontSize: 16, flexShrink: 0 }}
            />
            {msg.text}
          </div>
        )}

        {loadingUser || statsLoading ? (
          <div className="ap-spinner">
            <div className="ap-spin-ring" />
            Loading your dashboard…
          </div>
        ) : (
          <>
            {/* Welcome banner */}
            <div className="ap-welcome">
              <div className="ap-welcome-text">
                <div className="ap-welcome-greeting">Welcome back</div>
                <div className="ap-welcome-name">{userData?.name || "there"} 👋</div>
                <div className="ap-welcome-sub">Member since {memberSince}</div>
              </div>
              <div className="ap-welcome-badge">
                <div className="ap-welcome-badge-val">${Number(cartSubtotal || 0).toFixed(2)}</div>
                <div className="ap-welcome-badge-lbl">Cart Total</div>
              </div>
            </div>

            {/* Stats */}
            <div className="ap-stats">
              <div className="ap-stat" onClick={onGoToCart} style={{ cursor: "pointer" }}>
                <div className="ap-stat-icon g"><i className="ph ph-shopping-cart" /></div>
                <div>
                  <div className="ap-stat-val">{cartCount}</div>
                  <div className="ap-stat-lbl">Cart Items</div>
                </div>
              </div>
              <div className="ap-stat" onClick={onGoToWishlist} style={{ cursor: "pointer" }}>
                <div className="ap-stat-icon" style={{ background: "#fef2f2", color: "#dc2626" }}>
                  <i className="ph ph-heart" />
                </div>
                <div>
                  <div className="ap-stat-val">{wishlistCount}</div>
                  <div className="ap-stat-lbl">Wishlist Items</div>
                </div>
              </div>
              <div className="ap-stat" onClick={onGoToOrders} style={{ cursor: "pointer" }}>
                <div className="ap-stat-icon b"><i className="ph ph-package" /></div>
                <div>
                  <div className="ap-stat-val">{ordersCount}</div>
                  <div className="ap-stat-lbl">Total Orders</div>
                </div>
              </div>
            </div>

            {/* Profile info */}
            <div className="ap-section">
              <div className="ap-section-hd">
                <div>
                  <div className="ap-section-title">Profile Information</div>
                  <div className="ap-section-sub">Your personal details</div>
                </div>
                <button
                  className="ap-section-link"
                  style={{ background: "none", border: "none", cursor: "pointer" }}
                  onClick={onGoToSettings}
                >
                  Edit profile →
                </button>
              </div>
              <div className="ap-section-bd">
                <div className="ap-profile-grid">
                  <div>
                    <div className="ap-field-lbl">Full Name</div>
                    <div className="ap-field-val">{userData?.name || "—"}</div>
                  </div>
                  <div>
                    <div className="ap-field-lbl">Email</div>
                    <div className="ap-field-val mono">{userData?.email || "—"}</div>
                  </div>
                  <div>
                    <div className="ap-field-lbl">Account Status</div>
                    <div className="ap-status-dot">Active</div>
                  </div>
                  <div>
                    <div className="ap-field-lbl">Joined</div>
                    <div className="ap-field-val">{memberSince}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick actions — mirrors the sidebar exactly */}
            <div className="ap-section">
              <div className="ap-section-hd">
                <div>
                  <div className="ap-section-title">Quick Actions</div>
                  <div className="ap-section-sub">Navigate to key areas</div>
                </div>
              </div>
              <div className="ap-section-bd">
                <div className="ap-actions">

                  {/* Overview — already here, skip */}

                  <div className="ap-action" onClick={onGoToOrders} style={{ cursor: "pointer" }}>
                    <div className="ap-action-ico g"><i className="ph ph-package" /></div>
                    <div className="ap-action-text">
                      <span className="ap-action-name">My Orders</span>
                      <span className="ap-action-sub">Track &amp; view history</span>
                    </div>
                    {ordersCount > 0 && <span className="ap-action-badge">{ordersCount}</span>}
                  </div>

                  <div className="ap-action" onClick={onGoToWishlist} style={{ cursor: "pointer" }}>
                    <div className="ap-action-ico r"><i className="ph ph-heart" /></div>
                    <div className="ap-action-text">
                      <span className="ap-action-name">Wishlist</span>
                      <span className="ap-action-sub">Saved for later</span>
                    </div>
                    {wishlistCount > 0 && <span className="ap-action-badge">{wishlistCount}</span>}
                  </div>

                  <div className="ap-action" onClick={onGoToCart} style={{ cursor: "pointer" }}>
                    <div className="ap-action-ico b"><i className="ph ph-shopping-cart" /></div>
                    <div className="ap-action-text">
                      <span className="ap-action-name">Shopping Cart</span>
                      <span className="ap-action-sub">Ready to checkout</span>
                    </div>
                    {cartCount > 0 && <span className="ap-action-badge">{cartCount}</span>}
                  </div>

                  <div className="ap-action" onClick={onGoToDeliveryAddress} style={{ cursor: "pointer" }}>
                    <div className="ap-action-ico a"><i className="ph ph-map-pin" /></div>
                    <div className="ap-action-text">
                      <span className="ap-action-name">Add Delivery Address</span>
                      <span className="ap-action-sub">Add or update address</span>
                    </div>
                    <i className="ph ph-arrow-right" style={{ color: "#d1d5db", fontSize: 14, marginLeft: "auto" }} />
                  </div>

                  <div className="ap-action" onClick={onGoToSettings} style={{ cursor: "pointer" }}>
                    <div className="ap-action-ico a"><i className="ph ph-gear" /></div>
                    <div className="ap-action-text">
                      <span className="ap-action-name">Settings</span>
                      <span className="ap-action-sub">Preferences &amp; password</span>
                    </div>
                    <i className="ph ph-arrow-right" style={{ color: "#d1d5db", fontSize: 14, marginLeft: "auto" }} />
                  </div>

                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AccountOverview;